import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X, Car, Wrench, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { API } from '@/utils/api';
import axios from 'axios';

const ClientResponsePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responded, setResponded] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { loadRequest(); }, [token]);

  const loadRequest = async () => {
    try {
      const res = await axios.get(`${API}/public/respond/${token}`);
      setData(res.data);
      if (res.data.request.status !== 'pending') {
        setResponded(true);
        setResponseStatus(res.data.request.status);
      }
    } catch (err) {
      setError('Ce lien est invalide ou a expire.');
    } finally { setLoading(false); }
  };

  const handleResponse = async (status) => {
    try {
      await axios.post(`${API}/public/respond/${token}`, { status });
      setResponded(true);
      setResponseStatus(status);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la reponse');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md rounded-xl">
          <X className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </Card>
      </div>
    );
  }

  if (responded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md rounded-xl" data-testid="response-confirmation">
          {responseStatus === 'accepted' ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 mb-2">Travaux acceptes</h1>
              <p className="text-sm text-slate-500">Merci ! Nous procédons aux travaux supplémentaires sur votre véhicule.</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 mb-2">Travaux refuses</h1>
              <p className="text-sm text-slate-500">Bien note. Nous terminons les travaux initialement prevus.</p>
            </>
          )}
          <p className="text-xs text-slate-400 mt-4">{data?.garage?.name} - {data?.garage?.phone}</p>
        </Card>
      </div>
    );
  }

  const req = data?.request;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="p-6 max-w-md w-full rounded-xl space-y-6" data-testid="client-response-card">
        {/* Garage Header */}
        <div className="text-center border-b pb-4">
          <img src="/logo_garage.png" alt="Garage" className="h-12 mx-auto mb-2" />
          <h2 className="text-sm font-bold text-slate-900">{data?.garage?.name}</h2>
          <p className="text-xs text-slate-500">{data?.garage?.address}, {data?.garage?.city}</p>
        </div>

        {/* Client & Vehicle */}
        <div className="text-sm space-y-1">
          <p className="font-medium text-slate-900">Bonjour {data?.client_name},</p>
          {data?.vehicle_info && (
            <p className="text-slate-500 flex items-center gap-1"><Car className="w-4 h-4" /> {data.vehicle_info}</p>
          )}
        </div>

        {/* Work Description */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
            <Wrench className="w-4 h-4" /> Travaux supplementaires proposes
          </h3>
          <p className="text-sm text-amber-800" data-testid="work-description">{req?.description}</p>
          {req?.estimated_cost > 0 && (
            <p className="text-sm font-bold text-amber-900 flex items-center gap-1" data-testid="work-cost">
              <DollarSign className="w-4 h-4" /> Cout estime: CHF {req.estimated_cost.toFixed(2)}
            </p>
          )}
        </div>

        {/* Accept / Refuse Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => handleResponse('refused')}
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-14 text-base"
            data-testid="refuse-btn"
          >
            <X className="mr-2 w-5 h-5" /> Refuser
          </Button>
          <Button
            onClick={() => handleResponse('accepted')}
            className="bg-emerald-600 hover:bg-emerald-700 h-14 text-base"
            data-testid="accept-btn"
          >
            <Check className="mr-2 w-5 h-5" /> Accepter
          </Button>
        </div>

        <p className="text-xs text-center text-slate-400">
          En cas de questions, appelez-nous au {data?.garage?.phone}
        </p>
      </Card>
    </div>
  );
};

export default ClientResponsePage;
